import { get } from 'lodash-es';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { DEFAULT_DEMO_FULFILLMENT_PLAN_ID } from '../../demos/demo-orders';
import { DimensionUnit } from '../../enums/dimension-unit';
import { SellerSettings } from '../../enums/seller-settings/seller-settings';
import { SellerSettingsOptions } from '../../enums/seller-settings/seller-settings-options';
import { WeightUnit } from '../../enums/weight-unit';
import { getSellerFulfillmentProviderIdFromShipFromId, isShipFromExternal } from '../../store/csw/csw.fulfillment-plan-to-csw';

/**
 * @typedef {import('../../types/seller-data').SellerState} SellerState
 * @typedef {import('@shipstation/client-common/types/seller-data').SellerService} SellerService
 * @typedef {import('@shipstation/client-common/types/seller-data').PackageType} PackageType
 * @typedef {import('@shipstation/client-common/types/seller-data').ShipsuranceAccountInfo} ShipsuranceAccountInfo
 * @typedef {import('@shipstation/client-common/types/seller-data').SellerBrand} SellerBrand
 * @typedef {import('@shipstation/client-common/types/seller-data').SellerLabelProvider} SellerLabelProvider
 */

/** @param {SellerState} state @returns {string} */
export const selectSellerId = (state) => state.sellerId;

/** @param {SellerState} state @returns {boolean} */
export const selectPersonalShipsuranceAccount = (state) => get(state, 'hasPersonalShipsuranceAccount', false);

/** @param {SellerState} state @returns {ShipsuranceAccountInfo | Record<string, never>} */
export const selectShipsuranceAccountInfo = (state) => get(state, 'shipsuranceAccountInfo', emptyObject);

export const selectData = (state) => get(state, 'data', emptyObject);

/** @param {SellerState} state @returns {SellerBrand[]} */
export const selectSellerBrands = (state) => get(selectData(state), 'sellerBrands', emptyArray);

/**
 * @param {SellerState} state
 * @param {{ sellerBrandId: string }} props
 * @returns {SellerBrand | undefined}
 */
export const selectSellerBrandById = (state, props) =>
  get(selectData(state), 'sellerBrands', emptyArray).find(({ sellerBrandId }) => sellerBrandId === props.sellerBrandId);

/** @param {SellerState} state @returns {boolean} */
export const selectIsLoading = (state) => get(state, 'isLoading');

/** @param {SellerState} state @returns {SellerLabelProvider[]} */
export const selectLabelProviders = (state) => get(selectData(state), 'labelProviders', emptyArray);

// Reads the raw seller-side freight providers (one entry per connected freight provider).
// The auto-generated Global selector becomes `selectSellerFreightProviders`.
export const selectFreightProviders = (state) => get(selectData(state), 'sellerFreightProviders', emptyArray);

/**
 * @param {SellerState} state
 * @param {{ sellerLabelProviderId: string }} props
 * @returns {SellerLabelProvider | Record<string, never>}
 */
const selectLabelProviderBySellerLabelProviderId = createSelector(
  selectLabelProviders,
  (_, { sellerLabelProviderId }) => sellerLabelProviderId,
  (allSellerLabelProviders, sellerLabelProviderId) => {
    return allSellerLabelProviders.find((sp) => sp.sellerLabelProviderId === sellerLabelProviderId) || {};
  },
);
export const selectLabelProviderIsWalleted = createSelector(selectLabelProviderBySellerLabelProviderId, (sellerLabelProvider) => {
  return !!get(sellerLabelProvider, 'walletId');
});
export const selectAllFulfillmentProviders = (state) => get(selectData(state), 'fulfillmentProviders', emptyArray);
export const selectActiveFulfillmentProviders = createSelector(selectAllFulfillmentProviders, (allFulfillmentProviders) => {
  const activeFulfillmentProviders = allFulfillmentProviders.filter((sellerFulfillmentProvider) => sellerFulfillmentProvider.isActive);
  return activeFulfillmentProviders.length === 0 ? emptyArray : activeFulfillmentProviders;
});
export const selectFulfillmentServices = (state) => get(selectData(state), 'fulfillmentServices', emptyArray);

/** @param {SellerState} state @returns {SellerService[]} */
export const selectServices = (state) => get(selectData(state), 'services', emptyArray);

export const selectExtendedServices = (state) => get(selectData(state), 'extendedServices', emptyArray);

/** @param {SellerState} state @returns {SellerService[]} */
export const selectActiveServices = createSelector(selectServices, (services) => {
  const active = services.filter((s) => !s.isHidden);
  return active.length === 0 ? emptyArray : active;
});

/**
 * @param {SellerState} state
 * @param {{ serviceId: string }} props
 * @returns {SellerService | undefined}
 */
export const selectServiceById = (state, props) =>
  (selectServices(state) || emptyArray).find((service) => service.serviceId === props.serviceId);

/**
 * @param {SellerState} state
 * @param {{ serviceIds: string[] }} props
 * @returns {SellerService[]}
 */
export const selectServicesByIds = createCachedSelector(
  selectServices,
  (_, { serviceIds }) => JSON.stringify(serviceIds),
  (services, serviceIdsJSON) => {
    const serviceIds = JSON.parse(serviceIdsJSON);
    const result = serviceIds.reduce((acc, serviceId) => {
      const service = services.find((s) => s.serviceId === serviceId);
      if (service) {
        acc.push(service);
      }
      return acc;
    }, []);
    return result.length === 0 ? emptyArray : result;
  },
)((_, { serviceIds }) => JSON.stringify(serviceIds));

/**
 * @param {SellerState} state
 * @param {{ sellerLabelProviderId: string }} props
 * @returns {SellerService | undefined}
 */
export const selectServiceBySellerLabelProviderId = (state, props) =>
  (selectServices(state) || emptyArray).find((service) => service.sellerLabelProviderId === props.sellerLabelProviderId);

/**
 * @param {SellerState} state
 * @returns {PackageType[]}
 */
export const selectPackages = (state) => get(selectData(state), 'packages', emptyArray);
export const selectShipFroms = (state) => get(selectData(state), 'shipFroms', emptyArray);
export const selectFulfillmentProviderWarehouses = (state) => get(selectData(state), 'fulfillmentProviderWarehouses', emptyArray);
export const selectPromotionUpgrade = (state) => get(selectSubscriptionData(state), 'promotionUpgradeDetails', emptyObject) || emptyObject;
export const selectShipFromsByPropertyValue = createCachedSelector(
  selectShipFroms,
  (_, props) => props.property,
  (_, props) => props.value,
  (shipFroms, property, value) => {
    const result = shipFroms.filter((shipFrom) => shipFrom[property] === value);
    return result.length === 0 ? emptyArray : result;
  },
)((_, props) => `${props.property}:${props.value}`);
export const selectShipFromsById = (state, props) =>
  (selectShipFroms(state) || emptyArray).find((shipFrom) => shipFrom.shipFromId === props.shipFromId);
export const selectShipFromInventorySourceType = (state, props) =>
  get(selectShipFromsById(state, props), 'inventorySourceType', 'Unknown') || 'Unknown';
export const selectPresets = (state) => get(selectData(state), 'presets', emptyArray);
export const selectStoreErrorLogs = (state) => get(selectData(state), 'storeErrorLogs', emptyObject);
export const selectStores = (state) => get(selectData(state), 'stores', emptyArray);
export const selectStoreData = (state, props) => selectStores(state).find((store) => store.storeGuid === props.storeGuid);
export const selectStoreServiceMappings = (state, props) => get(selectStoreData(state, props), 'serviceMappings', emptyArray);
export const selectPackingSlipIdByStoreId = (state, props) => get(selectStoreData(state, props), 'packingSlip.packingSlipId');
export const selectShipmentTemplateIdByStoreId = (state, props) =>
  get(selectStoreData(state, props), 'shipmentEmailTemplate.emailTemplateId');
export const selectDeliveryTemplateIdByStoreId = (state, props) =>
  get(selectStoreData(state, props), 'deliveryEmailTemplate.emailTemplateId');
export const selectMarketplaceIdByStoreGuid = (state, props) => get(selectStoreData(state, props), 'marketplaceId');
export const selectPackingSlips = (state) => get(selectData(state), 'packingSlips', emptyArray);
export const selectDeliveryTemplates = (state) => get(selectData(state), 'deliveryTemplates', emptyArray);
export const selectShipmentTemplates = (state) => get(selectData(state), 'shipmentTemplates', emptyArray);
export const selectInventoryWarehouses = (state) => get(selectData(state), 'inventoryWarehouses', emptyArray);
export const selectInventoryWarehouseById = (state, { inventoryWarehouseId }) =>
  selectInventoryWarehouses(state).find((iw) => iw.inventoryWarehouseId === inventoryWarehouseId);
const selectInventoryWarehouseLocations = (state) => get(selectData(state), 'inventoryWarehousesLocations', emptyArray);
export const selectInventoryWarehouseLocationsById = (state, { inventoryWarehouseLocationId }) =>
  selectInventoryWarehouseLocations(state).find((iwl) => iwl.inventoryLocationId === inventoryWarehouseLocationId);
export const selectInventoryWarehouseLocationsByWarehouseId = createCachedSelector(
  selectInventoryWarehouseLocations,
  (_, { inventoryWarehouseId }) => inventoryWarehouseId,
  (locations, inventoryWarehouseId) => {
    const result = locations.filter((iwl) => iwl.inventoryWarehouseId === inventoryWarehouseId);
    return result.length === 0 ? emptyArray : result;
  },
)((_, { inventoryWarehouseId }) => inventoryWarehouseId ?? '');
const selectTotesData = (state) => get(state, 'totesData', emptyObject);
export const selectTotes = (state) => get(selectTotesData(state), 'totes', emptyArray);
export const selectTotesByWarehouseId = createCachedSelector(
  selectTotes,
  (_, { inventoryWarehouseId }) => inventoryWarehouseId,
  (totes, inventoryWarehouseId) => {
    const result = totes.filter((tote) => tote.inventoryWarehouseId === inventoryWarehouseId);
    return result.length === 0 ? emptyArray : result;
  },
)((_, { inventoryWarehouseId }) => inventoryWarehouseId ?? '');
export const selectToteById = (state, { toteId }) => selectTotes(state).find((tote) => tote.toteId === toteId);
export const selectTotesIsLoading = (state) => get(selectTotesData(state), 'isLoading', false);
export const selectTotesError = (state) => get(selectTotesData(state), 'error', null);
export const selectManagePresetsLoadingState = (state) => get(selectData(state), 'isLoading', false);
export const selectPackageSets = (state) => get(selectData(state), 'packageSets', emptyArray);
export const selectSellerSettings = (state) => get(selectData(state), 'settings', emptyArray);
export const selectSellerSetting = (state, { settingName }) => get(selectData(state), `settings['${settingName}']`);
export const selectSupportCountry = (state) => get(state, 'supportInfo.supportCountry');
export const selectLoadingStoreIds = (state) => get(state, 'loadingStoreIds', emptyArray);
export const selectSellerFeatures = (state) => get(state, 'features', emptyArray);
export const selectReturnReasons = (state) => get(selectData(state), 'returnReasons', emptyArray);
export const selectWallets = (state) => get(selectData(state), 'wallets', emptyArray);
export const selectDemoFulfillmentPlanId = (state) => get(selectData(state), 'demoFulfillmentPlanId', DEFAULT_DEMO_FULFILLMENT_PLAN_ID);
export const selectDefaultWeightUnit = (state) => {
  const value = selectSellerSetting(state, {
    settingName: SellerSettings.weightUnitOfMeasure.key,
  });
  if ([WeightUnit.Gram, WeightUnit.Grams].includes(value)) {
    return WeightUnit.Grams;
  }

  if ([WeightUnit.Ounce, WeightUnit.Ounces].includes(value)) {
    return WeightUnit.Ounces;
  }

  return WeightUnit.Ounces;
};

export const selectRawWeightUnitEnum = (state) => {
  const value = selectSellerSetting(state, {
    settingName: SellerSettings.weightUnitOfMeasure.key,
  });

  if ([WeightUnit.Gram, WeightUnit.Grams].includes(value)) {
    return 2;
  }

  if ([WeightUnit.Pound, WeightUnit.Pounds].includes(value)) {
    return 3;
  }

  if ([WeightUnit.Kilogram, WeightUnit.Kilograms].includes(value)) {
    return 4;
  }

  return 1;
};
export const selectSubscriptionPlanInfo = (state) => get(state, 'subscriptionPlanInfo');
export const selectCreateDateUTC = (state) => get(state, 'createDateUtc');
export const selectActivationDate = (state) => get(state, 'activationDate');
export const selectUsers = (state) => get(state, 'users', emptyArray);
export const selectUserById = (state, props) => (selectUsers(state) || emptyArray).find((usr) => usr.userId === props.userId);
export const selectHomeCountry = (state) => get(state, 'homeCountry');
export const selectDefaultCurrency = (state) => get(state, 'defaultCurrency');
export const selectSubscriptionCurrency = (state) => get(state, 'subscriptionCurrency');
export const selectAffiliateCode = (state) => get(state, 'affiliateCode');
export const selectReferralCode = (state) => get(state, 'referralCode');
export const selectCouponCode = (state) => get(state, 'couponCode');
export const selectShipmentsPerMonth = (state) => get(state, 'shipmentsPerMonth');
export const selectHasAcceptedDhlWalletTerms = (state) => get(state, 'dhlWalletAcceptTerms');
export const selectTimeZoneIANA = (state) => get(state, 'timeZoneIana', 'Etc/UTC');
export const selectIsSubscriptionExpired = (state) => {
  const subscriptionPlanInfo = selectSubscriptionPlanInfo(state);
  return get(subscriptionPlanInfo, 'isExpired');
};
export const selectPartnerSharedServices = (state) => get(selectData(state), 'partnerSharedServices', emptyArray);
export const selectFulfillmentProviderByProviderId = createSelector(
  selectActiveFulfillmentProviders,
  (_, { shipFromId }) => shipFromId,
  (fulfillmentProviders, shipFromId) => {
    if (shipFromId) {
      const sellerFulfillmentProviderId = isShipFromExternal(shipFromId)
        ? getSellerFulfillmentProviderIdFromShipFromId(shipFromId)
        : shipFromId;
      const providerFulfillment = fulfillmentProviders.find(
        (provider) => provider.sellerFulfillmentProviderId === sellerFulfillmentProviderId,
      );

      return providerFulfillment;
    }
  },
);

export const selectFulfillmentServicesBySellerFulfillmentProviderId = createSelector(
  selectActiveFulfillmentProviders,
  selectFulfillmentServices,
  (_, { sellerFulfillmentProviderId }) => sellerFulfillmentProviderId,
  (fulfillmentProviders, fulfillmentServices, sellerFulfillmentProviderId) => {
    const selectedFulfillmentProvider = fulfillmentProviders.find(
      (fulfillmentProvider) => fulfillmentProvider.sellerFulfillmentProviderId === sellerFulfillmentProviderId?.toString(),
    );
    return fulfillmentServices.filter(
      (fulfillmentService) => fulfillmentService.fulfillmentProviderId === selectedFulfillmentProvider?.fulfillmentProviderId,
    );
  },
);

export const selectFulfillmentServicesByShipFromId = createCachedSelector(
  selectFulfillmentServices,
  (state, { selectedShipFromId }) =>
    selectFulfillmentProviderByProviderId(state, {
      shipFromId: selectedShipFromId,
    }),
  (_, { selectedShipFromId }) => selectedShipFromId,
  (fulfillmentServices, selectedFulfillmentProvider, selectedShipFromId) => {
    if (selectedShipFromId) {
      const selectedFulfillmentProviderId = get(selectedFulfillmentProvider, 'fulfillmentProviderId');
      const providerFulfillmentServices = fulfillmentServices.filter(
        (fulfillmentService) => fulfillmentService.fulfillmentProviderId === selectedFulfillmentProviderId,
      );

      if (providerFulfillmentServices.length === 0) {
        return emptyArray;
      }

      return providerFulfillmentServices;
    }

    if (fulfillmentServices.length === 0) {
      return emptyArray;
    }

    return fulfillmentServices;
  },
)((_, { selectedShipFromId = '' }) => selectedShipFromId);

export const selectActiveStores = createSelector(selectStores, (stores) => {
  const activeStores = stores.filter((store) => store.isActive);

  if (activeStores.length === 0) {
    return emptyArray;
  }

  return activeStores;
});

export const selectActiveManualStores = createSelector(selectStores, (stores) => {
  const activeManualStores = stores.filter((store) => store.isActive && store.isManualStore);

  if (activeManualStores.length === 0) {
    return emptyArray;
  }

  return activeManualStores;
});

export const selectPackageType = (state, props) => {
  const packageTypes = selectPackages(state) || emptyArray;
  const packages = get(props, 'packages') || emptyArray;

  if (packages.length === 1) {
    const { packageTypeId } = packages[0];
    return packageTypes.find((packageType) => packageType.packageTypeId === packageTypeId);
  }

  // multipackage or no packages
};

export const getStoreInformations = createSelector(selectStores, (stores) => {
  if (stores.length === 0) {
    return emptyObject;
  }

  return stores.reduce(
    (acc, store) => ({
      ...acc,
      [store.storeGuid]: store,
    }),
    {},
  );
});

const getServices = (state) => get(selectData(state), 'services', emptyArray);

export const getShippingServices = createSelector(getServices, (services) => {
  if (services.length === 0) {
    return emptyArray;
  }

  return services.reduce(
    (acc, service) => ({
      ...acc,
      [service.serviceId]: service,
    }),
    {},
  );
});

export const selectRequestedServiceMapping = (state, { storeGuid, requestedServiceCode }) => {
  const matchingStore = get(selectData(state), 'stores', emptyArray).find((store) => store.storeGuid === storeGuid);
  const matchingStoreServiceMappings = get(matchingStore, 'serviceMappings', emptyArray);
  return matchingStoreServiceMappings.find((mapping) => mapping.requestedServiceCode === requestedServiceCode);
};

export const selectDefaultCustomData = createSelector(
  (state) => selectSellerSetting(state, { settingName: 'CustomsContentType' }),
  (state) => selectSellerSetting(state, { settingName: 'CustomsNonDelivery' }),
  (state) =>
    selectSellerSetting(state, {
      settingName: 'CustomsExportDeclarationNumber',
    }),
  (state) => selectSellerSetting(state, { settingName: 'InvoiceNumber' }),
  (state) => selectSellerSetting(state, { settingName: 'CustomsItemCountry' }),
  (state) => selectSellerSetting(state, { settingName: 'CustomsItemDescription' }),
  (state) => selectSellerSetting(state, { settingName: 'CustomsItemHarmonization' }),
  (state) => selectSellerSetting(state, { settingName: 'CustomsItemQty' }),
  (state) => selectSellerSetting(state, { settingName: 'CustomsItemValue' }),
  (state) =>
    selectSellerSetting(state, {
      settingName: SellerSettings.exemptionOrITN.key,
    }),
  (state) =>
    selectSellerSetting(state, {
      settingName: SellerSettings.defaultExemptionCode.key,
    }),
  (
    content,
    nonDelivery,
    exportDeclarationNumber,
    invoiceNumber,
    itemCountry,
    itemDescription,
    itemHarmonization,
    itemQty,
    itemValue,
    exemptionOrITN,
    defaultExemptionCode,
  ) => {
    const base = {
      content,
      nonDelivery,
      exportDeclarationNumber,
      invoiceNumber,
      itemCountry,
      itemDescription,
      itemHarmonization,
      itemQty,
      itemValue,
    };
    return exemptionOrITN === SellerSettingsOptions.uspsExemptionCode.value ? { ...base, exemptionCodeOrITN: defaultExemptionCode } : base;
  },
);

export const selectDefaultDimensionUnit = (state) => {
  const value = selectSellerSetting(state, {
    settingName: SellerSettings.dimensionUnitOfMeasure.key,
  });
  if ([DimensionUnit.Inch, DimensionUnit.Inches].includes(value)) {
    return DimensionUnit.Inches;
  }

  if ([DimensionUnit.Centimeter, DimensionUnit.Centimeters].includes(value)) {
    return DimensionUnit.Centimeters;
  }

  return DimensionUnit.Inches;
};

export const selectTags = (state) => get(selectData(state), 'tags', emptyArray);
export const selectTagsAsDictionary = createSelector(selectTags, (tags) => {
  if (tags.length === 0) {
    return emptyObject;
  }
  return tags.reduce((dict, tag) => {
    const { tagId } = tag;
    // switching this to a map causes MAJOR performance issues.
    // oxlint-disable-next-line no-param-reassign
    dict[tagId] = tag;
    return dict;
  }, {});
});

export const selectAccountManager = (state) => get(state, 'accountManager');

export const selectCompanyName = (state) => get(state, 'companyName');

export const selectFullName = (state) => get(state, 'name');

export const selectPhoneNumber = (state) => get(state, 'phone');

export const selectQuickshipAllowedForSeller = (state) =>
  selectSellerSetting(state, {
    settingName: SellerSettings.isQuickshipAllowed.key,
  })?.toLowerCase() === 'true';

export const selectShipperHQConnectedStoreGuids = (state) => get(selectData(state), 'shipperHQConnectedStores', emptyArray);

export const selectActiveUsers = createSelector(selectUsers, (users) => {
  const activeUsers = users.filter((user) => user.isActive);

  if (activeUsers.length === 0) {
    return emptyArray;
  }
  return activeUsers;
});

const selectUsersSortedByNameAll = createSelector(selectUsers, (users) =>
  users.length === 0 ? emptyArray : [...users].sort((a, b) => a.name.localeCompare(b.name)),
);

const selectUsersSortedByNameActive = createSelector(selectActiveUsers, (activeUsers) =>
  activeUsers.length === 0 ? emptyArray : [...activeUsers].sort((a, b) => a.name.localeCompare(b.name)),
);

export const selectUsersSortedByName = (state, props) => {
  const { includeInactive } = props || {};
  return includeInactive ? selectUsersSortedByNameAll(state) : selectUsersSortedByNameActive(state);
};

export const selectDefaultShipFromId = createSelector(
  selectShipFroms,
  (shipFroms) => shipFroms.find((shipFrom) => shipFrom.isDefault)?.shipFromId,
);

export const selectDefaultShipFrom = createSelector(selectShipFroms, (shipFroms) => shipFroms.find((shipFrom) => shipFrom.isDefault));

export const selectServiceCollections = (state) => state?.data?.serviceCollections ?? emptyArray;
export const selectServiceCollectionByGuid = (state, { serviceCollectionGuid }) =>
  selectServiceCollections(state).find((collection) => collection.serviceCollectionGuid === serviceCollectionGuid) ?? emptyObject;
export const selectPlanName = (state) => get(selectSubscriptionPlanInfo(state), 'planName', emptyArray);
export const selectPendingSubscription = (state) => get(selectSubscriptionPlanInfo(state), 'pendingSubscription', emptyObject);
export const selectPlanInfo = (state) => get(selectData(state), 'planInfo', emptyObject);
export const selectSubscriptionData = (state) => get(selectData(state), 'subscriptionData', emptyObject);
export const selectSubscriptionCoupons = createSelector(
  (state) => get(selectSubscriptionData(state), 'coupons', emptyArray),
  (coupons) => {
    if (coupons === emptyArray || coupons.length === 0) {
      return emptyArray;
    }
    return coupons.map((c) => c.coupon);
  },
);
export const selectFulfillmentOptions = (state) => get(state, 'data.fulfillmentProviderOptions', emptyArray);
export const selectEmail = (state) => get(state, 'email', '');
export const selectHasCreatedLabel = (state) => get(state, 'hasCreatedLabel', true);
export const selectBillingAddress = (state) => get(state, 'billingAddress');
export const selectZendeskOrganizationID = (state) => get(state, 'zendeskOrganizationID');

const selectIsBuyNowRawSetting = (state) => get(selectData(state), `settings['${SellerSettings.isBuyNow.key}']`);

export const selectBuyNowSetting = createSelector(selectIsBuyNowRawSetting, (setting) => {
  // Handle case where setting is stored as a JSON string instead of an object
  let parsedSetting = setting;
  if (typeof setting === 'string') {
    try {
      parsedSetting = JSON.parse(setting);
    } catch {
      parsedSetting = null;
    }
  }

  return {
    optedInToBuyNow: parsedSetting?.optedInToBuyNow ?? false,
    isBuyNowFlowCompleted: parsedSetting?.isBuyNowFlowCompleted ?? false,
  };
});
